import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Download, AlertTriangle, FileText } from "lucide-react";

import { BRAND } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignContract() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState("");
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/sign/${token}`)
      .then((res) => mounted && setContract(res.data))
      .catch((e) => mounted && setErr(e?.response?.data?.detail || "Contract not found."))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [token]);

  const onSign = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please type your full name to sign.");
      return;
    }
    if (!agree) {
      toast.error("Please confirm you agree to the terms.");
      return;
    }
    setSigning(true);
    try {
      const res = await axios.post(`${API}/sign/${token}`, {
        client_signature_name: name.trim(),
      });
      setContract(res.data);
      toast.success("Signed. A copy is on its way to your email.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed.");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="text-white/60 animate-spin" />
      </div>
    );
  }

  if (err || !contract) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md w-full rounded-3xl border border-white/10 bg-white/[0.02] p-10 text-center">
          <AlertTriangle size={32} className="mx-auto text-amber-400" />
          <h1 className="mt-4 font-display font-black text-2xl text-white">
            Contract unavailable
          </h1>
          <p className="mt-3 font-body text-white/60 text-sm">
            {err || "The link may have expired. Please reach out to cj@wereonesound.com for a new one."}
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black font-display font-bold hover:bg-brand-magenta hover:text-white"
          >
            Back to wereonesound.com
          </Link>
        </div>
      </div>
    );
  }

  const isSigned = contract.status === "signed";

  return (
    <div className="min-h-screen bg-background text-white">
      <header className="border-b border-white/10 bg-black/90 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt={BRAND.name} className="h-12 w-12" />
            <div className="hidden sm:block">
              <p className="font-display font-black text-sm text-brand-gradient">
                ONESOUND
              </p>
              <p className="font-body text-[9px] uppercase tracking-[0.25em] text-white/60">
                Services Agreement
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href={`${API}/sign/${token}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 bg-white/5 text-white text-xs uppercase tracking-[0.18em] hover:bg-white/10 transition-colors"
            >
              <Download size={14} /> PDF
            </a>
            {isSigned && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                <CheckCircle2 size={11} /> Signed
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-16">
        <div className="mb-8">
          <p className="font-body text-xs uppercase tracking-[0.3em] text-brand-magenta">
            For {contract.client_organization || contract.client_name}
          </p>
          <h1 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white">
            {isSigned
              ? "Agreement signed."
              : "Please review and sign your agreement."}
          </h1>
          <p className="mt-3 font-body text-white/60 max-w-2xl">
            {isSigned
              ? "Thank you. A copy of the signed PDF was emailed to you and to CJ at OneSound Productions. You can download the signed copy below."
              : "Read the agreement below. When you're ready, type your name in the signature panel and click Sign."}
          </p>

          {isSigned && (
            <a
              href={`${API}/sign/${token}/pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="download-signed-pdf"
              className="mt-6 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white text-black font-display font-bold tracking-tight hover:bg-brand-magenta hover:text-white transition-all duration-300 shadow-lg shadow-brand-magenta/20"
            >
              <Download size={18} /> Download Signed PDF
            </a>
          )}
        </div>

        {/* Render the contract HTML in a card */}
        <article
          data-testid="contract-render"
          className="rounded-3xl border border-white/10 bg-white overflow-hidden text-black"
        >
          <div
            className="contract-host px-4 sm:px-8 py-8 sm:py-12 [&_h1]:text-2xl sm:[&_h1]:text-3xl"
            dangerouslySetInnerHTML={{ __html: contract.html }}
          />
        </article>

        {/* Signing panel */}
        {!isSigned && (
          <form
            onSubmit={onSign}
            data-testid="sign-panel"
            className="mt-10 rounded-3xl border border-white/10 bg-gradient-to-br from-brand-magenta/10 via-transparent to-brand-orange/10 p-6 sm:p-10"
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-brand-magenta">
              Type-to-Sign
            </p>
            <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl text-white tracking-tight">
              Your signature
            </h2>
            <p className="mt-2 font-body text-white/60 text-sm max-w-md">
              Typing your full legal name below has the same legal effect as a
              wet signature.
            </p>

            <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-4 items-end">
              <div>
                <Label
                  htmlFor="sig-name"
                  className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60"
                >
                  Full Name *
                </Label>
                <Input
                  id="sig-name"
                  data-testid="sign-input-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={contract.client_name}
                  className="mt-2 bg-black/40 border-white/10 text-white h-14 rounded-xl text-2xl tracking-tight focus-visible:ring-brand-magenta"
                  style={{ fontFamily: "'Brush Script MT','Lucida Handwriting',cursive" }}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={signing}
                data-testid="sign-submit"
                className="rounded-full h-14 px-8 bg-white text-black hover:bg-brand-magenta hover:text-white font-display font-bold"
              >
                {signing ? <Loader2 size={16} className="animate-spin mr-2" /> : <FileText size={16} className="mr-2" />}
                {signing ? "Signing..." : "Sign Agreement"}
              </Button>
            </div>

            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={agree}
                onCheckedChange={(v) => setAgree(!!v)}
                className="mt-1"
                data-testid="sign-checkbox"
              />
              <span className="text-sm text-white/70 font-body">
                I have read and agree to the terms of this Audio / Music
                Production Services Agreement. I understand my typed signature
                is legally binding.
              </span>
            </label>
          </form>
        )}

        <footer className="mt-12 text-center font-body text-xs text-white/40">
          OneSound Productions, LLC · cj@wereonesound.com · 757-290-5084
        </footer>
      </main>
    </div>
  );
}
